import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import type { FC } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { css } from '@atlaskit/css';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { Node } from '@atlaskit/editor-prosemirror/model';
import { defaultNodeComponents } from '@atlaskit/renderer';

import { useScrollMargin } from './hooks/useScrollMargin';

import type { ChildInfo } from './VirtualizedTableCustomRangeExtractor';
import {
	useCustomRangeExtractor,
	UNSUPPORTED_CHILD_NODE_TYPES,
} from './VirtualizedTableCustomRangeExtractor';

type TableComponentProps = React.ComponentProps<typeof defaultNodeComponents.table>;

type VirtualizedTableComponentProps = Partial<TableComponentProps> & {
	children?: React.ReactNode;
	parentInfo?: { path: Node[] };
	childInfo?: ChildInfo;
};

export const VirtualizedTableComponent: FC<VirtualizedTableComponentProps> = ({
	children,
	parentInfo,
	childInfo,
	...otherTableProps
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const childrenArray = useMemo(() => React.Children.toArray(children), [children]);

	// Returns the main content container which serves as the scroll container
	const getScrollElement = useCallback(() => {
		return document.getElementById('AkMainContent');
	}, []);

	// scrollMargin is used to specify where the scroll offset should originate.
	// This value is the space between the beginning of the scrolling element
	// i.e. the top of AkMainContent and till the start of the table.
	const { scrollMargin } = useScrollMargin();

	// Tracks the Y-coordinate difference between the main content and table
	// Used for accurate remaining height calculations in virtual scrolling
	const [yCoordinateDifference, setYCoordinateDifference] = useState(0);

	useEffect(() => {
		const mainContent = document.getElementById('AkMainContent');
		const table = document.querySelector('[data-testid="renderer-table"]');

		if (!mainContent || !table) {
			return;
		}

		const mainContentRect = mainContent.getBoundingClientRect();
		const tableRect = table.getBoundingClientRect();

		const finalDifference = tableRect.top - mainContentRect.top;
		setYCoordinateDifference(finalDifference);
	}, []);

	const customRangeExtractor = useCustomRangeExtractor(childInfo);

	/**
	 * Core virtualizer configuration:
	 * - enabled: Always true for this component
	 * - count: Total number of table rows
	 * - estimateSize: Estimated height per row - used for initial calculations
	 * - overscan: Number of items to render outside viewport for smooth scrolling
	 * - scrollMargin: Offset for scroll positioning
	 * - rangeExtractor: Custom logic for determining which items to render
	 */
	const virtualizer = useVirtualizer({
		enabled: true,
		count: childrenArray.length,
		getScrollElement,
		estimateSize: () => 44, // Standard table row height in Confluence
		overscan: 5,
		scrollMargin,
		rangeExtractor: customRangeExtractor,
	});

	const virtualItems = virtualizer.getVirtualItems();

	//calculate column count from a table row element
	const calculateColumnCount = useCallback((rowElement: React.ReactNode): number => {
		let totalColumns = 0;

		const traverseElement = (element: React.ReactNode): void => {
			if (!React.isValidElement(element)) {
				return;
			}
			const props = element.props as { children?: React.ReactNode[] };
			if (props.children && Array.isArray(props.children)) {
				props.children.forEach((cell: React.ReactNode) => {
					if (React.isValidElement(cell)) {
						const cellProps = cell.props as { colspan?: number };
						totalColumns += cellProps?.colspan ?? 1;
					}
				});
			}
		};

		traverseElement(rowElement);
		return totalColumns;
	}, []);

	// column count calculation based on first row
	const columnCount = useMemo(() => {
		if (childrenArray.length > 0) {
			const rowElement = childrenArray[0];
			if (React.isValidElement(rowElement)) {
				return calculateColumnCount(rowElement);
			}
		}
		return 0;
	}, [childrenArray, calculateColumnCount]);

	// Renders only the visible table rows plus a spacer row to maintain total height
	const renderVirtualRows = useCallback(() => {
		const virtualRowsToRender = virtualItems.map((virtualRow) => childrenArray[virtualRow.index]);

		// Calculate remaining height below the last visible row
		// This spacer maintains the illusion of a full-height table
		const lastVirtualItem = virtualItems[virtualItems.length - 1];
		const remainingHeight = lastVirtualItem
			? virtualizer.getTotalSize() -
				(lastVirtualItem.start + lastVirtualItem.size) +
				yCoordinateDifference
			: 0;

		// Add spacer row to fill remaining height and maintain scroll behavior
		if (remainingHeight > 0 && columnCount > 0) {
			virtualRowsToRender.push(
				<tr key="spacer-row" style={{ height: `${remainingHeight}px` }}>
					<td key={`spacer-cell`} colSpan={columnCount} css={css({ height: '100%' })}></td>
				</tr>,
			);
		}

		return virtualRowsToRender;
	}, [virtualItems, childrenArray, virtualizer, yCoordinateDifference, columnCount]);

	const TableComponent = defaultNodeComponents.table;
	const tableWrapperRef = useRef<HTMLElement | null>(null);

	// Only enable virtualization for top-level tables
	const isVirtualizationEnabled = (parentInfo?.path?.length || 0) < 2;

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				action: 'render',
				actionSubject: 'virtualizedTable',
				attributes: {
					isVirtualizationEnabled,
					parentPathLength: parentInfo?.path?.length || 0,
					unsupportedChildNodeTypeCounts: UNSUPPORTED_CHILD_NODE_TYPES.reduce(
						(acc, nodeType) => ({
							...acc,
							[nodeType]: childInfo?.selfNodeTypeCounts?.[nodeType] || 0,
						}),
						{},
					),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isVirtualizationEnabled, parentInfo, childInfo]);

	useEffect(() => {
		const wrapper = document.querySelector('.pm-table-wrapper') as HTMLElement;
		const table = document.querySelector('[data-testid="renderer-table"]') as HTMLElement;

		if (wrapper) {
			// Set total height to accommodate all virtual rows
			wrapper.style.height = `${virtualizer.getTotalSize()}px`;
			wrapper.style.width = '100%';
			wrapper.style.position = 'relative';
			wrapper.style.overflow = 'hidden';
			tableWrapperRef.current = wrapper;
		}

		if (table) {
			table.style.height = `100%`;
		}
	}, [virtualizer, virtualItems]);

	if (!isVirtualizationEnabled) {
		return <TableComponent {...otherTableProps}>{children}</TableComponent>;
	}

	return <TableComponent {...otherTableProps}>{renderVirtualRows()}</TableComponent>;
};
